import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { errorMessage } from "../lib/api";
import { useLocalizedModule } from "../lib/localize";
import { readCachedModule, updateCachedModule, writeCachedModule } from "../offline/assetCache";
import { sendWrite, whenPersisted } from "../offline/writeQueue";
import { loadModule } from "../offline/moduleDownload";
import { track } from "../lib/events";
import { dayForModule, itemLabelKey } from "../lib/pdDayRoutes";
import { usePDDays } from "../pddays/usePDDays";
import { ContentView } from "../components/content/ContentView";
import { useAppOutlet } from "../components/Shell";
import type { QuizControl } from "../components/quiz/Quiz";
import { primaryButtonClass, secondaryButtonClass, Status } from "../components/ui";
import type { ModuleDetail } from "../types";

// A one-line description reads as a paragraph; several lines (one point per
// line) read as a bulleted list.
function ItemDescription({ text }: { text?: string }) {
  const lines = (text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) return null;
  if (lines.length === 1) {
    return <p className="mt-1 text-sm leading-relaxed text-app-copy">{lines[0]}</p>;
  }
  return (
    <ul className="mt-1.5 flex list-disc flex-col gap-1 ps-5 text-sm leading-relaxed text-app-copy marker:text-accent">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

// Keyed by module so moving on to the next module starts it afresh.
export function ModulePlayer() {
  const { moduleID = "" } = useParams();
  return <ModuleSteps key={moduleID} moduleID={moduleID} />;
}

function ModuleSteps({ moduleID }: { moduleID: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setHeaderTitle } = useAppOutlet();
  const [loadedModule, setModule] = useState<ModuleDetail | null>(null);
  const module = useLocalizedModule(loadedModule);
  // -1 opens the module's last item: stepping back from the next module.
  const [index, setIndex] = useState<number>(() =>
    Number.isInteger(location.state?.moduleIndex) && location.state.moduleIndex >= -1
      ? location.state.moduleIndex
      : 0
  );
  const [error, setError] = useState("");
  const [finishing, setFinishing] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [quizControl, setQuizControl] = useState<QuizControl | null>(null);
  // The module plays as one part of its PD Day: Previous/Next carry on into
  // the neighbouring modules and the progress bar spans the whole day.
  const { all, dayNumbers } = usePDDays();
  const day = dayForModule(all, moduleID);
  const dayModules = day?.modules ?? [];
  const position = dayModules.findIndex((entry) => entry.id === moduleID);
  const moduleNumber = position >= 0 ? position + 1 : 1;
  const prevModule = position > 0 ? dayModules[position - 1] : undefined;
  const nextModule = position >= 0 ? dayModules[position + 1] : undefined;
  const dayNumber = day ? dayNumbers[day.id] : undefined;
  const dayTitle = t("pdDayOrdinal", { number: dayNumber ?? "" });
  const [prevModuleLast, setPrevModuleLast] = useState<{ id: string; type: string } | null>(null);
  const prevModuleLastType =
    prevModule && prevModuleLast?.id === prevModule.id ? prevModuleLast.type : null;

  useEffect(() => {
    setHeaderTitle(dayTitle);
    return () => setHeaderTitle(null);
  }, [dayTitle, setHeaderTitle]);

  // "Previous: Module 1 Quiz" on a module's first item needs the type of the
  // previous module's last item; the background download keeps it on the phone.
  useEffect(() => {
    if (!prevModule) return;
    let active = true;
    void readCachedModule(prevModule.id).then((cached) => {
      const last = cached?.items[cached.items.length - 1];
      if (active && last) setPrevModuleLast({ id: prevModule.id, type: last.type });
    });
    return () => {
      active = false;
    };
  }, [prevModule]);

  useEffect(() => {
    // Falls back to the copy saved on this device when offline.
    loadModule(moduleID)
      .then((value) => {
        setModule(value);
        setIndex((current) =>
          current === -1
            ? Math.max(value.items.length - 1, 0)
            : current < value.items.length
              ? current
              : 0
        );
        track("page_opened", { module_id: moduleID, payload: { page: "module" } });
      })
      .catch((cause) => setError(errorMessage(cause)));
  }, [moduleID]);

  if (error) {
    return (
      <>
        <Status variant="error" title={t("moduleUnavailable")} message={error} />
      </>
    );
  }
  if (!module) {
    return <Status variant="loading" title={t("loading")} message={t("openingModule")} />;
  }
  if (!module.items.length) {
    return (
      <>
        <Status variant="empty" title={t("emptyModule")} message={t("emptyModuleHelp")} />
      </>
    );
  }

  const item = module.items[index];
  const nextItem = module.items[index + 1];
  const nextBlocked = item.block_next && item.progress.status !== "completed";
  const isQuiz = item.type === "quiz";
  const isLast = !nextItem;
  const prevItem = module.items[index - 1];
  const label = (type: string, number = moduleNumber) => t(itemLabelKey(type), { number });
  // Progress through the whole PD Day, by position: earlier modules' items
  // plus this one's so far.
  const dayTotal = dayModules.reduce((sum, entry) => sum + entry.item_count, 0);
  const before = dayModules
    .slice(0, Math.max(position, 0))
    .reduce((sum, entry) => sum + entry.item_count, 0);
  const percent =
    position >= 0 && dayTotal > 0
      ? Math.min(100, Math.round(((before + index) / dayTotal) * 100))
      : Math.round((index / module.items.length) * 100);
  // A quiz is confirmed by the same button that moves on; any other item
  // just moves on. The last item submits the module and carries on into the
  // next one, or finishes the PD Day.
  const confirming = isQuiz && !quizControl?.submitted;
  const advanceBlocked =
    finishing || (confirming ? !quizControl?.ready || quizControl.busy : !isLast && nextBlocked);
  const advanceLabel = confirming
    ? t("quizConfirm")
    : nextItem
      ? t("nextItem", { title: label(nextItem.type) })
      : nextModule
        ? t("nextModule")
        : t("finishPDDay", { number: dayNumber ?? "" });
  const previousLabel = prevItem
    ? t("previousItem", { title: label(prevItem.type) })
    : prevModule && prevModuleLastType
      ? t("previousItem", { title: label(prevModuleLastType, moduleNumber - 1) })
      : t("previous");

  function goToItem(nextIndex: number) {
    setIndex(nextIndex);
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: { ...location.state, moduleIndex: nextIndex },
    });
  }

  function goBack() {
    if (prevItem) goToItem(index - 1);
    else if (prevModule) {
      navigate(`/modules/${encodeURIComponent(prevModule.id)}`, { state: { moduleIndex: -1 } });
    }
  }

  async function advance() {
    if (advanceBlocked) return;
    setFinishing(true);
    setSubmissionError("");
    try {
      if (confirming) {
        const outcome = await quizControl?.submit();
        // "review": stay so the correct answers can be read; the next tap moves on.
        if (outcome !== "advance") return;
      }
      if (nextItem) goToItem(index + 1);
      else {
        const completedModule = confirming
          ? {
              ...module!,
              items: module!.items.map((entry) =>
                entry.id === item.id
                  ? { ...entry, progress: { status: "completed", percent: 100 } }
                  : entry
              ),
            }
          : module!;
        await writeCachedModule(completedModule);
        await sendWrite({
          method: "POST",
          path: `/api/teacher/modules/${encodeURIComponent(module!.id)}/submit`,
        });
        await whenPersisted();
        navigate(
          nextModule
            ? `/modules/${encodeURIComponent(nextModule.id)}`
            : `/modules/${encodeURIComponent(module!.id)}/complete`,
          { state: { moduleIndex: 0 } }
        );
      }
    } catch (cause) {
      setSubmissionError(errorMessage(cause));
    } finally {
      setFinishing(false);
    }
  }

  return (
    <div className="flex flex-col gap-5 pb-28 pt-2">
      {submissionError && (
        <p className="rounded-xl bg-danger-bg p-3 text-sm text-accent-strong" role="alert">
          {submissionError}
        </p>
      )}
      <div className="flex flex-col items-start gap-2.5">
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-app-field"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <div
            className="h-full rounded-full bg-accent transition-[width] duration-500"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      <ContentView
        key={item.id}
        moduleTitle={label(item.type)}
        onQuizControl={setQuizControl}
        item={item}
        moduleID={module.id}
        onComplete={() => {
          const markDone = (current: ModuleDetail): ModuleDetail => ({
            ...current,
            items: current.items.map((entry) =>
              entry.id === item.id
                ? { ...entry, progress: { status: "completed", percent: 100 } }
                : entry
            ),
          });
          setModule((current) => (current ? markDone(current) : current));
          // Keep the device copy in step, so reopening the module offline
          // doesn't show finished items as not started.
          void updateCachedModule(module.id, markDone);
        }}
      />

      {!isQuiz && (
        <section className="flex flex-col gap-1 rounded-xl bg-surface p-4 shadow-raised">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-accent">
            {label(item.type)}
          </span>
          {/* A video is named by its module ("Module 1: What are Values?"), not
              by its own authored title and description. */}
          <h2 className="text-lg font-semibold leading-snug text-app-ink">
            {item.type === "video" ? module.title : item.title}
          </h2>
          {item.type !== "video" && <ItemDescription text={item.description} />}
        </section>
      )}

      <div className="fixed bottom-0 left-1/2 z-40 w-full max-w-md -translate-x-1/2 bg-app-canvas/90 p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] backdrop-blur-md">
        <nav className="flex justify-between gap-3">
          <button
            className={`${secondaryButtonClass} flex min-w-0 flex-1 items-center justify-center gap-1 text-sm`}
            disabled={(!prevItem && !prevModule) || finishing || quizControl?.busy}
            onClick={goBack}
          >
            <ArrowLeft size={18} className="shrink-0 rtl:rotate-180" />
            <span className="truncate">{previousLabel}</span>
          </button>
          <button
            className={`${primaryButtonClass} flex min-w-0 flex-1 items-center justify-center gap-1 text-sm`}
            disabled={advanceBlocked}
            onClick={() => void advance()}
          >
            <span className="truncate">{advanceLabel}</span>
            {finishing || quizControl?.busy ? (
              <Loader2 size={18} className="shrink-0 animate-spin" />
            ) : isLast && !nextModule && !confirming ? (
              <CheckCircle2 size={18} className="shrink-0" />
            ) : (
              <ArrowRight size={18} className="shrink-0 rtl:rotate-180" />
            )}
          </button>
        </nav>
        {!isLast && nextBlocked && !isQuiz && (
          <p className="mt-2 text-center text-xs font-medium text-warning-text">
            {t("completeRequiredItem")}
          </p>
        )}
      </div>
    </div>
  );
}
